import { useQuery } from '@tanstack/react-query';
import {
  getUsageOverview,
  getCreditsSummary,
  getUsageAlerts,
  type UsageOverview,
  type CreditSummaryOrg,
  type UsageAlert,
} from '../api/admin';

// ── Helpers ──

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPrice(cents: number): string {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(0)}`;
}

function fmtCost(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

// ── Component ──

export function UsageMonitoringPage() {
  const overviewQuery = useQuery<UsageOverview>({
    queryKey: ['usage-overview'],
    queryFn: () => getUsageOverview(30),
  });

  const creditsQuery = useQuery<{ orgs: CreditSummaryOrg[] }>({
    queryKey: ['usage-credits-summary'],
    queryFn: getCreditsSummary,
  });

  const alertsQuery = useQuery<{ alerts: UsageAlert[] }>({
    queryKey: ['usage-alerts'],
    queryFn: getUsageAlerts,
  });

  const overview = overviewQuery.data;
  const credits = creditsQuery.data;
  const alerts = alertsQuery.data;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Usage Monitoring</h1>
        <p className="text-slate-600 mt-2">
          Platform-wide credit consumption, estimated costs, subscription revenue, and organization usage
        </p>
      </div>

      {/* Loading State */}
      {overviewQuery.isLoading && (
        <div className="text-center py-12 text-slate-400">Loading usage data...</div>
      )}

      {/* Summary Cards - Row 1 */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          <SummaryCard
            label="Total Credits Consumed"
            value={fmtNum(overview.total_credits)}
            color="purple"
          />
          <SummaryCard
            label="Total Tokens Used"
            value={fmtNum(overview.total_tokens)}
            color="blue"
          />
          <SummaryCard
            label="Total Requests"
            value={fmtNum(overview.total_requests)}
            color="green"
          />
          <SummaryCard
            label="Avg Latency"
            value={`${overview.avg_latency_ms.toFixed(0)}ms`}
            color="amber"
          />
        </div>
      )}

      {/* Summary Cards - Row 2: Revenue & Cost */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <SummaryCard
            label="Estimated AI Cost (30d)"
            value={fmtCost(overview.total_estimated_cost)}
            color="purple"
            sub="Actual LLM provider costs"
          />
          <SummaryCard
            label="Monthly Subscription Revenue"
            value={fmtPrice(overview.total_subscription_fee_cents)}
            color="green"
            sub={`${overview.total_subscriptions} active subscription${overview.total_subscriptions !== 1 ? 's' : ''}`}
          />
          <SummaryCard
            label="Active Orgs Today"
            value={overview.active_orgs_today.toString()}
            color="blue"
          />
        </div>
      )}

      {/* Daily Usage Table */}
      {overview && overview.daily.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Daily Usage (Last 30 Days)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3 text-right">Credits</th>
                  <th className="px-6 py-3 text-right">Tokens</th>
                  <th className="px-6 py-3 text-right">Requests</th>
                  <th className="px-6 py-3 text-right">Est. Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overview.daily.map((day) => (
                  <tr key={day.date} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3 font-medium text-slate-900">{day.date}</td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(day.credits)}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(day.tokens)}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(day.requests)}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtCost(day.estimated_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Models Table */}
      {overview && overview.top_models.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Top Models by Credit Consumption</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3">Provider</th>
                  <th className="px-6 py-3">Model</th>
                  <th className="px-6 py-3 text-right">Credits</th>
                  <th className="px-6 py-3 text-right">Tokens</th>
                  <th className="px-6 py-3 text-right">Requests</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overview.top_models.map((model, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3 text-slate-700">{model.provider}</td>
                    <td className="px-6 py-3 font-medium text-slate-900">{model.model}</td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(model.credits)}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(model.tokens)}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtNum(model.requests)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Org Credit Usage Leaderboard */}
      {credits && credits.orgs.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Organization Credit & Subscription Usage</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3">Organization</th>
                  <th className="px-6 py-3">Plan</th>
                  <th className="px-6 py-3 text-right">Fee</th>
                  <th className="px-6 py-3">Credits Used / Limit</th>
                  <th className="px-6 py-3 w-36">Progress</th>
                  <th className="px-6 py-3 text-right">Days Left</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {credits.orgs.map((org) => (
                  <tr key={org.org_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-slate-900">{org.name}</div>
                      {org.byok_enabled && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">BYOK</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getPlanColor(org.plan)}`}>
                        {org.plan_name}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {fmtPrice(org.plan_price_cents)}
                      {org.plan_price_cents > 0 && (
                        <span className="text-[10px] text-slate-400">/{org.billing_interval === 'yearly' ? 'yr' : 'mo'}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-700">
                      {fmtNum(org.credits_used)} / {fmtNum(org.credits_limit)}
                      {org.bonus > 0 && (
                        <span className="ml-2 text-xs text-purple-600">(+{fmtNum(org.bonus)} bonus)</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${getProgressColor(org.percent_used)}`}
                            style={{ width: `${Math.min(org.percent_used, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600 w-10 text-right">
                          {org.percent_used}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right text-slate-700">
                      {org.days_remaining}d
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts Panel */}
      {alerts && alerts.alerts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-lg font-semibold text-slate-900">Alerts</h2>
            <span className="ml-auto text-xs font-medium text-slate-500">
              {alerts.alerts.length} alert{alerts.alerts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {alerts.alerts.map((alert, idx) => (
              <div key={idx} className="px-6 py-4 flex items-start gap-4">
                <span className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${getSeverityDotColor(alert.severity)}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{alert.org_name}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getSeverityBadgeColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    <span className="text-xs text-slate-500">{alert.type}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {alerts && alerts.alerts.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="text-slate-400 text-sm">No active alerts - all organizations within limits</div>
        </div>
      )}
    </div>
  );
}

// ── Helper Components ──

function SummaryCard({ label, value, color, sub }: { label: string; value: string; color: 'purple' | 'blue' | 'green' | 'amber'; sub?: string }) {
  const colorClasses = {
    purple: 'border-purple-200 bg-purple-50',
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    amber: 'border-amber-200 bg-amber-50',
  };

  const textClasses = {
    purple: 'text-purple-700',
    blue: 'text-blue-700',
    green: 'text-green-700',
    amber: 'text-amber-700',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className={`inline-block px-3 py-1 rounded-lg border text-xs font-semibold mb-3 ${colorClasses[color]} ${textClasses[color]}`}>
        {label}
      </div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ── Utility Functions ──

function getPlanColor(plan: string): string {
  switch (plan.toLowerCase()) {
    case 'enterprise':
      return 'bg-purple-50 text-purple-700';
    case 'business':
      return 'bg-amber-50 text-amber-700';
    case 'pro':
      return 'bg-blue-50 text-blue-700';
    case 'starter':
      return 'bg-emerald-50 text-emerald-700';
    case 'free':
      return 'bg-slate-50 text-slate-700';
    default:
      return 'bg-emerald-50 text-emerald-700';
  }
}

function getProgressColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-purple-600';
}

function getSeverityDotColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    default:
      return 'bg-blue-500';
  }
}

function getSeverityBadgeColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-50 text-red-700';
    case 'warning':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-blue-50 text-blue-700';
  }
}
